import { Loader2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useAddInvoiceCoupon } from '~entities/payment/api/add-invoice-coupon';
import { useGetInvoiceCoupons } from '~entities/payment/api/get-invoice-coupons';
import { useUpdateInvoiceCoupons } from '~entities/payment/api/update-invoice-coupons';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

interface InvoiceDiscountManagerProps {
  invoiceId: string;
  className?: string;
  onDiscountChange?: (discountAmount: number) => void;
}

export const InvoiceDiscountManager = ({
  invoiceId,
  className,
  onDiscountChange,
}: InvoiceDiscountManagerProps) => {
  const [couponAmount, setCouponAmount] = useState<string>('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [currentDiscount, setCurrentDiscount] = useState<number | undefined>();

  const { data: currencyData } = useGetCurrency();

  const { data: invoiceCoupons, isLoading: isLoadingCoupons } =
    useGetInvoiceCoupons({
      invoiceId,
    });

  const { mutateAsync: addInvoiceCoupon, isPending: isAddingCoupon } =
    useAddInvoiceCoupon();
  const { mutateAsync: updateInvoiceCoupons, isPending: isUpdatingCoupons } =
    useUpdateInvoiceCoupons();

  // Set initial coupon amount when data loads
  useEffect(() => {
    if (invoiceCoupons?.totalDiscount !== undefined) {
      setCurrentDiscount(invoiceCoupons.totalDiscount);
      setCouponAmount(invoiceCoupons.totalDiscount.toFixed(2).toString());
      onDiscountChange?.(invoiceCoupons.totalDiscount);
    }
  }, [invoiceCoupons?.totalDiscount, onDiscountChange]);

  const validateCouponAmount = (value: string): boolean => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      setCouponError('Coupon amount must be 0 or greater');
      return false;
    }
    setCouponError(null);
    return true;
  };

  const handleAddOrUpdateCoupon = async () => {
    if (!validateCouponAmount(couponAmount)) {
      return;
    }

    try {
      const value = parseFloat(couponAmount);

      // If there's already a discount (including 0), update it; otherwise add new
      if (invoiceCoupons?.totalDiscount !== undefined) {
        const response = await updateInvoiceCoupons({
          invoiceId,
          value,
        });
        const updatedDiscount = response.invoice.totalDiscount;
        setCurrentDiscount(updatedDiscount);
        onDiscountChange?.(updatedDiscount);
      } else {
        const response = await addInvoiceCoupon({
          invoiceId,
          value,
        });
        const updatedDiscount = response.invoice.totalDiscount;
        setCurrentDiscount(updatedDiscount);
        onDiscountChange?.(updatedDiscount);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update discount');
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      // Set discount to 0 to remove it
      const response = await updateInvoiceCoupons({
        invoiceId,
        value: 0,
      });
      const updatedDiscount = response.invoice.totalDiscount;
      setCurrentDiscount(updatedDiscount);
      setCouponAmount('0.00');
      onDiscountChange?.(updatedDiscount);
    } catch (error) {
      console.error(error);
      toast.error('Failed to remove discount');
    }
  };

  const hasDiscount =
    currentDiscount !== undefined && currentDiscount > 0;
  const hasAmountChanged =
    currentDiscount !== undefined &&
    parseFloat(couponAmount) !== parseFloat(currentDiscount.toFixed(2));

  return (
    <div className={className}>
      <div className='mb-2 flex items-center justify-between'>
        <h3 className='text-sm font-medium'>Discount</h3>
        {currentDiscount !== undefined && (
          <span className='text-sm font-medium text-green-600'>
            Current Discount: {currencyData?.symbol}
            {currentDiscount.toFixed(2)}
          </span>
        )}
      </div>

      {/* Add/Update Discount */}
      <div className='mb-3 rounded-md border p-3'>
        <div className='flex items-end gap-2'>
          <div className='flex-1'>
            <Label
              htmlFor='discount-amount'
              className='text-xs text-muted-foreground'
            >
              Discount Amount
            </Label>
            <Input
              id='discount-amount'
              type='number'
              placeholder='0.00'
              value={couponAmount}
              onChange={(e) => {
                setCouponAmount(e.target.value);
                validateCouponAmount(e.target.value);
              }}
              className='mt-1'
              step='0.01'
              min='0'
              disabled={isLoadingCoupons}
            />
          </div>
          <Button
            onClick={handleAddOrUpdateCoupon}
            disabled={
              isLoadingCoupons ||
              !!couponError ||
              isAddingCoupon ||
              isUpdatingCoupons ||
              (invoiceCoupons?.totalDiscount !== undefined && !hasAmountChanged)
            }
            size='sm'
          >
            {isAddingCoupon || isUpdatingCoupons ? (
              <Loader2 className='size-4 animate-spin' />
            ) : invoiceCoupons?.totalDiscount !== undefined ? (
              'Update'
            ) : (
              'Add'
            )}
          </Button>
          {hasDiscount && (
            <Button
              onClick={handleRemoveCoupon}
              variant='outline'
              size='sm'
              disabled={isLoadingCoupons || isUpdatingCoupons}
            >
              <Trash2 className='size-4' />
            </Button>
          )}
        </div>
        {couponError && (
          <p className='mt-1 text-xs text-red-500'>{couponError}</p>
        )}
      </div>
    </div>
  );
};
